import {useCallback, useEffect, useState} from 'react'
import {Banner} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {parseShareCommand} from '@github-ui/one-copilot-composer/existing'

import {resolveAgentProfileForAPITask} from '../agents/resolve-agent-profile'
import {useCurrentUserOptional} from '../contexts/CurrentUserContext'
import type {HydratedAPITask} from '../types/api-task'
import {generateTaskPathForRepo} from '../utils/generate-task-path'
import {getTaskSharingPermissionError, isTaskSharingEnabled} from '../utils/task-sharing'
import {fetchTask} from './use-task'
import {useUpdateTaskSharingStatus} from './use-update-task-sharing-status'

interface ShareCommandResult {
  message: string
  variant: 'success' | 'critical'
  href?: string
  /** The task the command ran against; the result is shown only while it is active. */
  taskId: string | undefined
}

function ShareCommandFeedback({result, onDismiss}: {result: ShareCommandResult; onDismiss: () => void}) {
  // `Banner` carries no live region, so the outcome of a command that never
  // becomes a turn would otherwise reach a screen reader only by chance.
  useEffect(() => {
    announce(result.message, {assertive: result.variant === 'critical'})
  }, [result])

  return (
    <Banner
      variant={result.variant}
      title="Session sharing"
      hideTitle
      description={result.message}
      onDismiss={onDismiss}
      primaryAction={
        result.href ? (
          <Banner.PrimaryAction as="a" href={result.href} target="_blank" rel="noopener noreferrer">
            View shared session (opens in a new tab)
          </Banner.PrimaryAction>
        ) : undefined
      }
    />
  )
}

function sharingRequestError(error: unknown, fallback: string): string {
  if (error instanceof FetchCAPIError) {
    switch (error.status) {
      case 401:
        return 'Your session has expired. Sign in again to change sharing settings.'
      case 403:
        return "You don't have permission to access this session's sharing settings."
      case 404:
        return 'This session could not be found or you no longer have access to it.'
    }
  }
  return fallback
}

export function useShareCommandHandler({taskId, onDismissFeedback}: {taskId?: string; onDismissFeedback?: () => void}) {
  const [result, setResult] = useState<ShareCommandResult>()
  const {mutateAsync: updateSharingStatus} = useUpdateTaskSharingStatus({taskId})
  // Ownership follows each host's own sharing controls: the task view knows the
  // viewer's ID and compares IDs, while other hosts only know the login.
  const currentUserId = useCurrentUserOptional()?.currentUser?.id
  const dismissFeedback = useCallback(() => {
    setResult(undefined)
    onDismissFeedback?.()
  }, [onDismissFeedback])

  const applyShareCommand = useCallback(
    async (text: string) => {
      const command = parseShareCommand(text)
      if (!command) return false
      // The task can change while this awaits, so each outcome names the task the
      // command ran against rather than being reported on whichever is active.
      const report = (outcome: Omit<ShareCommandResult, 'taskId'>) => setResult({...outcome, taskId})
      const notifyError = (message: string) => report({message, variant: 'critical'})
      if (command.kind === 'invalid') {
        notifyError('Invalid usage. Use /share, /share link, or /share off.')
        return true
      }
      const login = getCurrentUserLogin()
      if (!login) {
        notifyError('You must be signed in to share a session.')
        return true
      }
      if (!isTaskSharingEnabled()) {
        notifyError('Session sharing is not available.')
        return true
      }
      if (!taskId) {
        notifyError('This session is not attached to a web task and cannot be shared here.')
        return true
      }

      let task: HydratedAPITask
      try {
        task = await fetchTask({taskId})
      } catch (error) {
        notifyError(sharingRequestError(error, 'Could not load session sharing settings. Please try again.'))
        return true
      }
      const permissionError = getTaskSharingPermissionError({
        currentUserIsOwner:
          currentUserId === undefined
            ? task.creator.login?.toLowerCase() === login.toLowerCase()
            : task.creator.id === currentUserId,
        hasRepository: Boolean(task.repository?.id),
        supportsSharing: resolveAgentProfileForAPITask(task).privateSessions,
      })
      if (permissionError) {
        notifyError(permissionError)
        return true
      }

      try {
        await updateSharingStatus({taskId, sharing_status: command.kind === 'share' ? 'shared' : 'unshared'})
      } catch (error) {
        notifyError(sharingRequestError(error, 'Could not change session sharing settings. Please try again.'))
        return true
      }

      if (command.kind === 'unshare') {
        report({message: 'Session sharing turned off.', variant: 'success'})
      } else {
        report({
          message: 'Session shared with repository readers.',
          variant: 'success',
          href: generateTaskPathForRepo({taskId}),
        })
      }
      return true
    },
    [currentUserId, taskId, updateSharingStatus],
  )

  const currentResult = result?.taskId === taskId ? result : undefined
  return {
    applyShareCommand,
    feedback: currentResult ? <ShareCommandFeedback result={currentResult} onDismiss={dismissFeedback} /> : null,
  }
}
